import { useEffect, useState } from "react";
import { appwriteConfig, client, functions } from "@/lib/appwrite/config";
import {
  createDealStatus,
  acceptDealCancellation,
  acceptDisputeRefund,
  declineDealCancellation,
  confirmDealDelivery,
  declineDealAdjustment,
  getDealAdjustments,
  getDealById,
  getDealByBuyerAndSeller,
  markDealDelivered,
  markDealShipped,
  quoteDealAdjustment,
  raiseDealDispute,
  requestDealCancellation,
  requestDealAdjustment,
  resolveDealDispute,
  startDealWork,
  updateDealStatus,
} from "@/lib/appwrite/api";
import { DealAdjustmentDocument, DealDocument } from "@/types";
import { notificationService } from "@/services/NotificationService";
import {
  createDealPaymentOrder,
  createDealAdjustmentPaymentOrder,
  getDealAdjustmentPaymentPricing,
  verifyCashfreePayment,
  calculateSellerPayout,
  calculatePaymentBreakdown as calculatePaymentBreakdownService,
  PaymentBreakdown,
  SellerPayoutBreakdown,
  DealAdjustmentPaymentPricing,
} from "@/lib/cashfree/paymentService";
import { INITIAL_USER, useUserContext } from "@/context/AuthContext";
import { toast } from "@/components/ui";
import { useNavigate } from "react-router-dom";
import {
  getUserTrustScore,
  logTrustScoreEvent,
} from "@/lib/appwrite/trustScore";
import {
  X,
  Shield,
  Wallet,
  CreditCard,
  AlertCircle,
  Loader,
  RefreshCw,
  Crown,
  Gift,
} from "lucide-react";
import { ProtectionChecklist } from "@/components/deal/ProtectionChecklist";
import { SellerPaymentRequestModal } from "@/components/deal/SellerPaymentRequestModal";
import {
  isDeliveryDetailsBlocked,
  violationTracker,
} from "@/lib/utils/blockedPatterns";
import {
  getViolationType,
  isUserBanned,
  recordUserViolation,
} from "@/lib/appwrite/userViolationAPI";
import { useSignOutAccount } from "@/lib/react-query/queries";
import PaymentPhoneField from "@/components/shared/PaymentPhoneField";
import {
  paymentContactFromIdentifier,
  requiresPaymentPhone,
} from "@/lib/cashfree/paymentContact";

interface DealTrackerProps {
  buyerId: string;
  sellerId: string;
  currentUserId: string;
  dealId?: string;
  postId?: string;
  postCategory?: string;
  isCollapsible?: boolean;
  onDealUpdate?: (deal: DealDocument) => void;
}

// Temporarily hidden. Set to true when buyer-side deal revision is ready to return.
const ENABLE_BUYER_DEAL_REVISION = false;

// PaymentBreakdown interface is now imported from paymentService

declare global {
  interface Window {
    Cashfree: any;
  }
}

export const DealTracker = ({
  buyerId,
  sellerId,
  currentUserId,
  dealId,
  postId,
  postCategory,
  isCollapsible = false,
  onDealUpdate,
}: DealTrackerProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [deal, setDeal] = useState<DealDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<"buyer" | "seller" | "observer">("observer");
  const [isUpdating, setIsUpdating] = useState(false);

  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [showCancellationForm, setShowCancellationForm] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancellationTimeLeft, setCancellationTimeLeft] = useState<number | null>(null);
  const [adjustments, setAdjustments] = useState<DealAdjustmentDocument[]>([]);
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [adjustmentDescription, setAdjustmentDescription] = useState("");
  const [adjustmentQuote, setAdjustmentQuote] = useState("");
  const [isAdjustmentUpdating, setIsAdjustmentUpdating] = useState(false);
  const [adjustmentPaymentPreview, setAdjustmentPaymentPreview] = useState<{
    adjustmentId: string;
    pricing: DealAdjustmentPaymentPricing;
  } | null>(null);
  const { user } = useUserContext();
  const [isReleasingPayment, setIsReleasingPayment] = useState(false);
  const navigate = useNavigate();

  // New state for payment modal
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentBreakdown, setPaymentBreakdown] =
    useState<PaymentBreakdown | null>(null);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState({
    address: "",
    mobile: "",
    landmark: "",
  });

  const { setUser, setIsAuthenticated } = useUserContext();
  const { mutate: signOut } = useSignOutAccount();
  const [isSubmittingAddress, setIsSubmittingAddress] = useState(false);
  const [showReviseDialog, setShowReviseDialog] = useState(false);
  const [buyerTrustScore, setBuyerTrustScore] = useState<number | null>(null);
  const [isCheckingTrustScore, setIsCheckingTrustScore] = useState(false);
  const [showSellerPaymentModal, setShowSellerPaymentModal] = useState(false);
  const [showProtectionChecklist, setShowProtectionChecklist] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [paymentPhone, setPaymentPhone] = useState("");
  const [sellerPayoutPreview, setSellerPayoutPreview] =
    useState<SellerPayoutBreakdown | null>(null);
  const hasResolvedPostCategory = !postId || Boolean(postCategory?.trim());
  const normalizedPostCategory = postCategory?.trim().toLowerCase();
  const isServiceDeal = normalizedPostCategory === "services" || normalizedPostCategory === "service";
  const isPhysicalDeal = ["physical-goods", "physical goods", "physical"].includes(normalizedPostCategory || "");
  const isWorkDeal = isServiceDeal || ["digital", "digital-products"].includes(normalizedPostCategory || "");
  const dealUpdateVersion = deal?.updatedAt ?? deal?.$updatedAt ?? (deal as any)?.updatedAtUtc;

  useEffect(() => {
    const amount = Number(deal?.requestedAmount || deal?.amount || 0);
    if (role !== "seller" || !Number.isFinite(amount) || amount <= 0) {
      setSellerPayoutPreview(null);
      return;
    }

    let active = true;
    calculateSellerPayout(amount, sellerId, deal?.$id)
      .then((preview) => {
        if (active) setSellerPayoutPreview(preview);
      })
      .catch(() => {
        if (active) setSellerPayoutPreview(null);
      });

    return () => {
      active = false;
    };
  }, [deal?.$id, deal?.amount, deal?.requestedAmount, role, sellerId]);

  useEffect(() => {
    let unsubscribe: () => void;
    let isMounted = true;

    const initDeal = async () => {
      try {
        setLoading(true);

        // When postId is provided, look for specific deal for that post (include completed)
        // When no postId, look for any deal between users (include completed for tracker display)
        const existingDeal = dealId
          ? await getDealById(dealId)
          : (await getDealByBuyerAndSeller(buyerId, sellerId, postId, true)) ||
            (await getDealByBuyerAndSeller(sellerId, buyerId, postId, true));

        if (existingDeal?.wasInitiated) {
          if (isMounted) {
            setDeal(existingDeal);
            setRole(
              currentUserId === existingDeal.buyerId
                ? "buyer"
                : currentUserId === existingDeal.sellerId
                  ? "seller"
                  : "observer"
            );
          }

          unsubscribe = client.subscribe(
            `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.dealStage}.documents.${existingDeal.$id}`,
            (response: any) => {
              if (response.payload && isMounted) {
                setDeal(response.payload);
                onDealUpdate?.(response.payload);
              }
            }
          );
          return;
        }

        // An explicit monitoring route is view-only if the deal no longer exists.
        // Never create a replacement deal from a support screen.
        if (dealId) return;

        const newDeal = await createDealStatus({
          buyerId,
          sellerId,
          postId,
          wasInitiated: true,
        });

        if (isMounted && newDeal) {
          setDeal(newDeal);
          setRole(
            currentUserId === newDeal.buyerId
              ? "buyer"
              : currentUserId === newDeal.sellerId
                ? "seller"
                : "observer"
          );

          unsubscribe = client.subscribe(
            `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.dealStage}.documents.${newDeal.$id}`,
            (response: any) => {
              if (response.payload && isMounted) {
                setDeal(response.payload);
                onDealUpdate?.(response.payload);
              }
            }
          );
        }
      } catch (error) {
        console.error("Error initializing deal:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const timer = setTimeout(() => {
      initDeal();
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      if (unsubscribe) unsubscribe();
    };
  }, [buyerId, sellerId, currentUserId, dealId, postId, onDealUpdate]);

  useEffect(() => {
    if (!deal?.$id) {
      setAdjustments([]);
      return;
    }
    let active = true;
    getDealAdjustments(deal.$id)
      .then((items) => { if (active) setAdjustments(items); })
      .catch((error) => console.error("Unable to load order additions", error));
    return () => { active = false; };
  }, [deal?.$id, dealUpdateVersion]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (
      deal?.status === "delivered" &&
      currentUserId === deal?.buyerId &&
      deal?.disputeStatus !== "pending"
    ) {
      const deliveryTime = new Date(deal.deliveredAt || deal.updatedAt).getTime();
      const expiryTime = deliveryTime + 24 * 60 * 60 * 1000;

      const updateTimeLeft = () => {
        const now = Date.now();
        const timeDiff = Math.max(0, expiryTime - now);
        setTimeLeft(timeDiff);

      };

      updateTimeLeft();
      interval = setInterval(updateTimeLeft, 10000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [
    deal?.status,
    deal?.deliveredAt,
    deal?.updatedAt,
    deal?.disputeStatus,
    currentUserId,
    deal?.buyerId,
  ]);

  useEffect(() => {
    if (deal?.status !== "cancellation-requested" || !deal.cancellationResponseDueAt) {
      setCancellationTimeLeft(null);
      return;
    }

    const update = () => {
      setCancellationTimeLeft(Math.max(0, new Date(deal.cancellationResponseDueAt!).getTime() - Date.now()));
    };
    update();
    const interval = setInterval(update, 10000);
    return () => clearInterval(interval);
  }, [deal?.status, deal?.cancellationResponseDueAt]);

  const checkReviseEligibility = async () => {
    if (!isBuyer || !deal || deal.status !== "paid") return;

    setIsCheckingTrustScore(true);
    try {
      const trustData = await getUserTrustScore(buyerId);
      setBuyerTrustScore(trustData?.score || 0);

      if (trustData?.score >= 5000) {
        // Automatically revert to negotiation
        await handleReviseDeal(trustData.score);
      } else {
        // Show upgrade modal
        setShowReviseDialog(true);
      }
    } catch (error) {
      console.error("Error checking trust score:", error);
      toast({
        title: "Error",
        description: "Could not check eligibility. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCheckingTrustScore(false);
    }
  };

  // Handle automatic deal revision for eligible users
  const handleReviseDeal = async (trustScore: number) => {
    if (!deal || trustScore < 5000) return;

    try {
      // Revert deal status to negotiation and set isDealRevised flag
      await updateDealStatus(deal.$id, {
        status: "negotiation",
        amount: "0",
        requestedAmount: "0",
        isDealRevised: true,
        revisedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      setDeal((prev) =>
        prev
          ? {
              ...prev,
              status: "negotiation",
              amount: "0",
              requestedAmount: "0",
              isDealRevised: true,
              revisedAt: new Date().toISOString(),
            }
          : null
      );
      // Notify seller about revision
      await notificationService.createNotification({
        title: "Deal Revised",
        body: `Buyer has revised the deal for ₹${deal.amount}. You can now renegotiate the terms.`,
        type: "deal",
        recipient_id: sellerId,
        sender_id: buyerId,
      });

      toast({
        title: "Deal Revised Successfully",
        description: "You can now renegotiate the terms with the provider.",
      });
    } catch (error) {
      console.error("Error revising deal:", error);
      toast({
        title: "Error",
        description: "Could not revise deal. Please try again.",
        variant: "destructive",
      });
    }
  };

  const validateDeliveryAddress = async (address: any) => {
    const errors: string[] = [];
    // Check if user is banned first
    try {
      const userIsBanned = await isUserBanned(currentUserId);
      if (userIsBanned) {
        toast({
          title: "Account Suspended",
          description:
            "Your account has been suspended due to repeated violations of our community guidelines. Contact contact@busiconn.com for assistance.",
          variant: "destructive",
        });
        signOut();
        setIsAuthenticated(false);
        setUser(INITIAL_USER);
        navigate("/sign-in?banned=true");
        return ["Account suspended"];
      }
    } catch (error) {
      console.error("Error checking ban status:", error);
    }

    // Check for email patterns in address and landmark fields
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;

    if (emailRegex.test(address.address)) {
      errors.push("Email addresses are not allowed in address field");
    }
    if (emailRegex.test(address.landmark)) {
      errors.push("Email addresses are not allowed in landmark field");
    }

    // Check for mobile numbers in address field using existing violation tracker
    const addressBlockResult = isDeliveryDetailsBlocked(address.address || "");
    if (addressBlockResult.isBlocked && isBuyer) {
      try {
        // Record the violation in the database
        const violationType = getViolationType(addressBlockResult.reason);
        const violationResult = await recordUserViolation(currentUserId, {
          violationType,
          messageText: address.address,
          severity: addressBlockResult.severity,
        });

        const { violation } = violationResult;

        // Determine appropriate message based on violation stage
        let title = "Invalid Address Format";
        let description =
          "Mobile numbers are not allowed in address field. Use the mobile field instead.";

        switch (violation.violationStage) {
          case "banned":
            title = "Account Suspended";
            description =
              "Your account has been suspended due to repeated violations. Contact support if you believe this is an error.";
            break;

          case "flagged":
            title = "Account Flagged";
            const flagDate = violation.flaggedUntil
              ? new Date(violation.flaggedUntil).toLocaleDateString("en-GB")
              : "N/A";
            description = `Mobile numbers are not allowed in address field. Your account is now flagged until ${flagDate}. Further violations will result in account suspension. (Total violations: ${violation.violationCount})`;
            break;

          default: // 'normal'
            if (addressBlockResult.severity === "violation") {
              title = "Content Violation";
              description = `Mobile numbers are not allowed in address field. This violation has been recorded. (Total violations: ${violation.violationCount})`;
            } else {
              title = "Content Warning";
              description = `Mobile numbers are not allowed in address field. Please follow our community guidelines.`;
            }
        }

        toast({
          title,
          description,
          variant: "destructive",
        });
      } catch (dbError) {
        console.error("Error recording violation to database:", dbError);
      }

      errors.push(
        "Mobile numbers are not allowed in address field. Use the mobile field instead."
      );
    }

    // Check for mobile numbers in landmark field using existing violation tracker
    const landmarkBlockResult = isDeliveryDetailsBlocked(
      address.landmark || ""
    );
    if (landmarkBlockResult.isBlocked && isBuyer) {
      try {
        // Record the violation in the database
        const violationType = getViolationType(landmarkBlockResult.reason);
        const violationResult = await recordUserViolation(currentUserId, {
          violationType,
          messageText: address.landmark,
          severity: landmarkBlockResult.severity,
        });

        const { violation } = violationResult;

        // Determine appropriate message based on violation stage
        let title = "Invalid Landmark Format";
        let description =
          "Mobile numbers are not allowed in landmark field. Use the mobile field instead.";

        switch (violation.violationStage) {
          case "banned":
            title = "Account Suspended";
            description =
              "Your account has been suspended due to repeated violations. Contact support if you believe this is an error.";
            break;

          case "flagged":
            title = "Account Flagged";
            const flagDate = violation.flaggedUntil
              ? new Date(violation.flaggedUntil).toLocaleDateString("en-GB")
              : "N/A";
            description = `Mobile numbers are not allowed in landmark field. Your account is now flagged until ${flagDate}. Further violations will result in account suspension. (Total violations: ${violation.violationCount})`;
            break;

          default: // 'normal'
            if (landmarkBlockResult.severity === "violation") {
              title = "Content Violation";
              description = `Mobile numbers are not allowed in landmark field. This violation has been recorded. (Total violations: ${violation.violationCount})`;
            } else {
              title = "Content Warning";
              description = `Mobile numbers are not allowed in landmark field. Please follow our community guidelines.`;
            }
        }

        toast({
          title,
          description,
          variant: "destructive",
        });
      } catch (dbError) {
        console.error("Error recording violation to database:", dbError);

        // Fallback to local tracking if database fails
        const userStatus = violationTracker.recordViolation(
          currentUserId,
          landmarkBlockResult.severity
        );

        let title = "Invalid Landmark Format";
        let description =
          "Mobile numbers are not allowed in landmark field. Use the mobile field instead.";

        if (userStatus.isBanned) {
          title = "Account Suspended";
          description =
            "Your account has been suspended due to repeated violations. Contact support if you believe this is an error.";
        } else if (userStatus.isFlagged) {
          title = "Account Flagged";
          description = `${description} Your account is now flagged. Further violations will result in account suspension. (Warnings: ${userStatus.warnings}, Violations: ${userStatus.violations})`;
        } else if (landmarkBlockResult.severity === "warning") {
          description = `${description} This is a warning - please follow our community guidelines.`;
        }

        toast({
          title,
          description,
          variant: "destructive",
        });
      }

      errors.push(
        "Mobile numbers are not allowed in landmark field. Use the mobile field instead."
      );
    }

    // Validate the dedicated mobile field format (10 digits starting with 6-9)
    const mobileRegex = /^[6-9]\d{9}$/;
    if (address.mobile && !mobileRegex.test(address.mobile)) {
      errors.push(
        "Please enter a valid 10-digit mobile number in the mobile field"
      );
    }

    return errors;
  };

  const isBuyer = role === "buyer";
  const isSeller = role === "seller";
  const buyerPayableAmount = paymentBreakdown
    ? paymentBreakdown.originalAmount +
      paymentBreakdown.platformFee -
      (applyDiscount ? paymentBreakdown.discount : 0)
    : 0;

  // UPDATED: Use new escrow-based payment breakdown calculation from paymentService
  // This uses dynamic fee formulas: buyerFee = max(0.8%, 1.6% - 0.1% * log10(P))
  // Also supports first-deal-free promotion for amounts up to ₹2,000

  const handleStatusUpdate = async (newStatus: string) => {
    if (!deal || isUpdating) return;
    setIsUpdating(true);

    try {
      if (newStatus === "completed" && isBuyer) {
        setIsReleasingPayment(true);

        // Import seller payout calculation from escrow system
        const totalAmount = Number(deal.requestedAmount || deal.amount);

        // Calculate seller payout using escrow formulas
        // Seller fee: max(2%, 5% - 0.3% * log10(P)) - higher amounts get lower fees
        const sellerPayoutBreakdown = await calculateSellerPayout(totalAmount, sellerId, deal.$id);
        const sellerAmount = sellerPayoutBreakdown.youWillReceive;

        try {
          // The backend transactionally queues the payout. Its worker submits
          // to Cashfree, and the webhook/reconciler confirms the terminal state.
          const payoutResponse = await functions.createExecution(
            appwriteConfig.cashfreeReleasePayment,
            JSON.stringify({
              dealId: deal.$id,
              sellerId: sellerId,
              sellerAmount: sellerAmount,
              buyerId: buyerId,
            })
          );

          // Parse the response
          let payoutResult;
          try {
            payoutResult = JSON.parse(payoutResponse.responseBody);
          } catch (parseError) {
            console.error("Failed to parse payout response:", parseError);
            throw new Error("Invalid payout response format");
          }

          if (
            payoutResponse.responseStatusCode < 200 ||
            payoutResponse.responseStatusCode >= 300
          ) {
            throw new Error(
              payoutResult.error || "Cashfree payout could not be initiated"
            );
          }

          if (payoutResult.success) {
            // Show success toast
            toast({
              title: "Payment Initiated",
              description:
                "Payment is being processed. The deal will be completed automatically once the transfer is confirmed.",
            });
          } else {
            // Show error toast
            toast({
              title: "Payment release failed!",
              description: `Payment release failed: ${payoutResult.error}`,
              variant: "destructive",
            });
          }
        } catch (paymentError: any) {
          console.error("Payment release failed:", paymentError);

          // Revert status on error, Removing code to update the status code here

          toast({
            title: "Payment Processing Error",
            description: `Technical error during payment release: ${paymentError.message}. Support has been notified.`,
            variant: "destructive",
          });
        }

        setIsReleasingPayment(false);
      } else {
        if (["in-progress", "shipped", "delivered"].includes(newStatus)) {
          const updatedDeal = newStatus === "in-progress"
            ? await startDealWork(deal.$id)
            : newStatus === "shipped"
              ? await markDealShipped(deal.$id)
              : isPhysicalDeal
                ? await confirmDealDelivery(deal.$id)
                : await markDealDelivered(deal.$id);
          setDeal(updatedDeal);
          return;
        }
        // Handle other status updates normally
        await updateDealStatus(deal.$id, {
          status: newStatus,
          amount: deal.amount,
          requestedAmount: deal.requestedAmount || "0",
          updatedAt: new Date().toISOString(),
        });

        notificationService.createNotification({
          title: "Deal Update",
          body:
            newStatus === "delivered"
              ? "Item delivered successfully!"
              : newStatus === "completed"
                ? "Deal Completed Successfully!"
                : newStatus === "payment-processing"
                  ? "Payment is being processed!"
                  : "Dispute Raised!",
          type: "deal",
          recipient_id: isSeller ? buyerId : sellerId,
          sender_id: currentUserId,
        });
      }
    } catch (error) {
      console.error("Status update failed:", error);
      notificationService.createNotification({
        title: "Update Failed",
        body: "Failed to update deal status. Please try again.",
        type: "error",
        recipient_id: currentUserId,
        sender_id: "system",
      });
    } finally {
      setIsReleasingPayment(false);
      setIsUpdating(false);
    }
  };

  const handleDisputeSubmit = async () => {
    const reason = disputeReason.trim();
    if (!deal || isUpdating || reason.length < 20 || reason.length > 500) {
      toast({
        title: "Add more detail",
        description: "Dispute reason must be between 20 and 500 characters.",
        variant: "destructive",
      });
      return;
    }
    setIsUpdating(true);
    try {
      const updatedDeal = await raiseDealDispute(deal.$id, reason);
      setDeal(updatedDeal);
      setShowDisputeForm(false);
      setDisputeReason("");
    } catch (err) {
      console.error("Dispute submission failed", err);
      toast({
        title: "Dispute not submitted",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleResolveDispute = async () => {
    if (!deal || isUpdating) return;
    setIsUpdating(true);
    try {
      const updatedDeal = await resolveDealDispute(deal.$id);
      setDeal(updatedDeal);
    } catch (err) {
      console.error("Failed to resolve dispute", err);
      toast({
        title: "Dispute not resolved",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancellationRequest = async () => {
    const reason = cancellationReason.trim();
    if (!deal || isUpdating || reason.length < 10 || reason.length > 500) {
      toast({
        title: "Add a cancellation reason",
        description: "Cancellation reason must be between 10 and 500 characters.",
        variant: "destructive",
      });
      return;
    }
    setIsUpdating(true);
    try {
      const updatedDeal = await requestDealCancellation(deal.$id, reason);
      setDeal(updatedDeal);
      setShowCancellationForm(false);
      setCancellationReason("");
      toast({ title: "Cancellation requested", description: "The provider has 24 hours to respond." });
    } catch (err) {
      toast({
        title: "Cancellation not requested",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancellationDecision = async (accept: boolean) => {
    if (!deal || isUpdating) return;
    setIsUpdating(true);
    try {
      const updatedDeal = accept
        ? await acceptDealCancellation(deal.$id)
        : await declineDealCancellation(deal.$id);
      setDeal(updatedDeal);
      toast({
        title: accept ? "Refund started" : "Cancellation declined",
        description: accept
          ? "Cashfree is processing the payer's refund."
          : "The deal can continue.",
      });
    } catch (err) {
      toast({
        title: "Unable to update cancellation",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAcceptDisputeRefund = async () => {
    if (!deal || isUpdating) return;
    setIsUpdating(true);
    try {
      const updatedDeal = await acceptDisputeRefund(deal.$id);
      setDeal(updatedDeal);
      toast({ title: "Refund started", description: "Cashfree is processing the payer's refund." });
    } catch (err) {
      toast({
        title: "Refund not started",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Opens the new seller payment request modal with protection checklist
  const handleRequestPayment = () => {
    if (!deal || isUpdating) return;
    setShowSellerPaymentModal(true);
  };

  // Handle the actual payment request submission from the modal
  const handlePaymentRequestSubmit = async (requestedAmount: string) => {
    if (!deal) throw new Error("No deal found");
    const numericRequestedAmount = Number(requestedAmount);
    if (!Number.isFinite(numericRequestedAmount) || numericRequestedAmount < 100) {
      throw new Error("Enter an amount of at least ₹100");
    }

    const normalizedRequestedAmount = numericRequestedAmount.toString();

    const updatedDeal = await updateDealStatus(deal.$id, {
      requestedAmount: normalizedRequestedAmount,
      status: "payment-requested",
      updatedAt: new Date().toISOString(),
    });
    if (!updatedDeal) {
      throw new Error("The payment request could not be applied to this deal");
    }

    setDeal(updatedDeal);

    await notificationService.createNotification({
      title: "Payment Requested",
      body: `Provider requested ₹${normalizedRequestedAmount}`,
      type: "deal",
      recipient_id: buyerId,
      sender_id: currentUserId,
    });

    toast({
      title: "Payment Requested",
      description: "Your payment request has been sent to the payer.",
    });
  };

  // UPDATED: Handle payment modal opening with new escrow-based calculation
  const handlePaymentClick = async () => {
    if (!deal || !deal.requestedAmount) return;

    try {
      // Reset protection states for new modal open
      setShowProtectionChecklist(false);
      setShowPaymentDetails(false);

      // Use new service function that includes first-deal-free check and dynamic fees
      const breakdown = await calculatePaymentBreakdownService(
        parseFloat(deal.requestedAmount),
        buyerId,
        true // Calculate the available discount; the checkbox controls its use.
      );

      setPaymentBreakdown(breakdown);
      setShowPaymentDialog(true);
      setApplyDiscount(false);

      // Start protection checklist animation after a short delay
      setTimeout(() => {
        setShowProtectionChecklist(true);
      }, 100);
    } catch (error) {
      console.error("Error calculating payment breakdown:", error);
      toast({
        title: "Error",
        description: "Could not calculate payment details. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handler for when protection checklist animation completes
  const handleBuyerProtectionComplete = () => {
    setShowPaymentDetails(true);
  };

  // New function to handle confirmed payment
  const handleConfirmPayment = async () => {
    if (!deal || !paymentBreakdown || isProcessing) return;

    const paymentContact = paymentContactFromIdentifier(user.emailOrPhone, paymentPhone);
    if (!paymentContact.phone) {
      toast({
        title: "Mobile number required",
        description: "Enter a valid mobile number to continue with Cashfree.",
        variant: "destructive",
      });
      return;
    }

    // Check if Cashfree SDK is loaded
    if (!window.Cashfree) {
      toast({
        title: "Payment System Error",
        description: "Payment system is not ready. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);

    try {
      const cashfreeOrderIdTemp = `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // The checkbox is the only control that applies the trust-score discount.
      const buyerPaymentAmount = buyerPayableAmount;

      // Create the Cashfree order with buyer's payment amount
      const orderResponse = await createDealPaymentOrder(
        deal.$id,
        buyerPaymentAmount, // Buyer pays this amount
        {
          id: buyerId,
          name: user.name,
          email: paymentContact.email,
          phone: paymentContact.phone,
        },
        deal.sellerId,
        `${window.location.origin}/payment/callback?order_id=${cashfreeOrderIdTemp}&source=deal&entity_id=${deal.$id}&buyer_id=${buyerId}&seller_id=${deal.sellerId}`
      );

      if (!orderResponse.success || !orderResponse.payment_session_id) {
        throw new Error(
          orderResponse.error || "Failed to create payment session"
        );
      }

      const cashfreeOrderId = orderResponse.order_id;

      if (!cashfreeOrderId) {
        throw new Error("No order ID received from Cashfree");
      }

      // Initialize payment
      const cashfree = new window.Cashfree({
        mode: "production",
      });

      const checkoutOptions = {
        paymentSessionId: orderResponse.payment_session_id,
        returnUrl: `${window.location.origin}/payment/callback?order_id=${cashfreeOrderId}&source=deal&entity_id=${deal.$id}&buyer_id=${buyerId}&seller_id=${deal.sellerId}`,
        redirectTarget: "_modal",
      };

      // Close the dialog before opening payment
      setShowPaymentDialog(false);

      // Handle payment completion
      cashfree
        .checkout(checkoutOptions)
        .then(async (response: any) => {
          try {
            if (response.error) {
              throw new Error(response.error.message || "Payment failed");
            }

            if (response.paymentDetails) {
              const verificationOrderId = cashfreeOrderId;

              // Verify payment on our backend
              const isValid = await verifyCashfreePayment({
                orderId: verificationOrderId,
                paymentId: response.paymentDetails.paymentId,
              });

              if (!isValid) {
                throw new Error("Payment verification failed");
              }

              // Update deal status with ORIGINAL amount (seller gets original amount)
              await updateDealStatus(deal.$id, {
                amount: paymentBreakdown.originalAmount.toString(), // Seller gets original amount
                status: "paid",
                cashfreeOrderId: cashfreeOrderId,
                cashfreePaymentId: response.paymentDetails.paymentId,
                paymentVerifiedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              });

              // If discount was applied, deduct points from buyer's trust score
              if (applyDiscount && paymentBreakdown.discount > 0) {
                try {
                  const pointsUsed = Math.round(paymentBreakdown.discount * 10);

                  await logTrustScoreEvent(
                    buyerId,
                    "discount_used",
                    -pointsUsed,
                    `Discount of ₹${paymentBreakdown.discount.toFixed(2)} (${pointsUsed} pts) used for deal payment`,
                    deal.$id
                  );

                  console.log(
                    `Trust score updated: ${pointsUsed} points deducted for discount usage`
                  );
                } catch (trustScoreError) {
                  console.error(
                    "Failed to update trust score:",
                    trustScoreError
                  );
                }
              }

              // Notify seller with original amount
              await notificationService.createNotification({
                title: "Payment Received",
                body: `₹${paymentBreakdown.originalAmount} secured in escrow`,
                type: "payment",
                recipient_id: deal.sellerId,
                sender_id: buyerId,
              });

              toast({
                title: "Payment Successful",
                description: `₹${buyerPaymentAmount.toFixed(2)} paid successfully${applyDiscount ? ` (Saved ₹${paymentBreakdown.discount.toFixed(2)})` : ""}`,
              });

              navigate(`/chats/${deal.sellerId}`);
            }
          } catch (error) {
            console.error("Payment processing failed:", error);

            await notificationService.createNotification({
              title: "Payment Error",
              body: "Contact support for assistance",
              type: "error",
              recipient_id: buyerId,
              sender_id: "system",
            });

            toast({
              title: "Payment Verification Failed",
              description: "Our team will review your payment shortly",
              variant: "destructive",
            });
          }
        })
        .catch((error: any) => {
          console.error("Cashfree checkout error:", error);
          toast({
            title: "Payment Failed",
            description: error.message || "Payment could not be completed",
            variant: "destructive",
          });
        });
    } catch (error) {
      console.error("Payment failed:", error);

      toast({
        title: "Payment Failed",
        description:
          error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });

      notificationService.createNotification({
        title: "Payment Failed",
        body: error instanceof Error ? error.message : "Please try again",
        type: "error",
        recipient_id: buyerId,
        sender_id: "system",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading || !deal) return null;

  const showReviseButton =
    ENABLE_BUYER_DEAL_REVISION &&
    isBuyer &&
    deal.status === "paid" &&
    !deal.deliveryAddress;

  const showDisputeInfo =
    deal.status === "dispute-raised" && deal.disputeReason;
  const showResolveButton =
    isSeller &&
    deal.status === "dispute-raised" &&
    deal.disputeStatus === "pending";

  const showDisputeButton =
    deal.status === "delivered" &&
    isBuyer &&
    (timeLeft === null || timeLeft > 0) &&
    deal.disputeStatus !== "pending";

  const showCompletionButton =
    isBuyer &&
    deal.status === "delivered" &&
    (!deal.disputeStatus || deal.disputeStatus === "resolved");
  const latestAdjustment = adjustments[0];
  const activeAdjustment = latestAdjustment
    && ["requested", "quoted", "payment-pending"].includes(latestAdjustment.status)
    && new Date(latestAdjustment.expiresAtUtc).getTime() > Date.now()
      ? latestAdjustment
      : undefined;
  const showCancellationButton =
    isBuyer && !activeAdjustment && (deal.status === "paid" || deal.status === "delivery-details-added" || deal.status === "in-progress");

  const refreshAdjustment = async () => {
    if (!deal) return;
    setAdjustments(await getDealAdjustments(deal.$id));
  };

  const handleAdjustmentRequest = async () => {
    if (!deal || adjustmentDescription.trim().length < 5) return;
    setIsAdjustmentUpdating(true);
    try {
      const created = await requestDealAdjustment(deal.$id, adjustmentDescription.trim());
      setAdjustments((items) => [created, ...items]);
      setAdjustmentDescription("");
      setShowAdjustmentForm(false);
      toast({ title: "Request sent", description: "The provider can now quote the additional amount." });
    } catch (error) {
      toast({ title: "Unable to add to order", description: error instanceof Error ? error.message : "Please try again.", variant: "destructive" });
    } finally {
      setIsAdjustmentUpdating(false);
    }
  };

  const handleAdjustmentQuote = async () => {
    if (!deal || !activeAdjustment) return;
    const amount = Number(adjustmentQuote);
    if (!Number.isFinite(amount) || amount < 1) return;
    setIsAdjustmentUpdating(true);
    try {
      const quoted = await quoteDealAdjustment(deal.$id, activeAdjustment.$id, amount);
      setAdjustments((items) => [quoted, ...items.filter((x) => x.$id !== quoted.$id)]);
      setAdjustmentQuote("");
      toast({ title: "Additional amount requested", description: `The buyer can now pay ₹${amount.toFixed(2)}.` });
    } catch (error) {
      toast({ title: "Unable to send quote", description: error instanceof Error ? error.message : "Please try again.", variant: "destructive" });
    } finally {
      setIsAdjustmentUpdating(false);
    }
  };

  const handleAdjustmentDecline = async () => {
    if (!deal || !activeAdjustment) return;
    setIsAdjustmentUpdating(true);
    try {
      const declined = await declineDealAdjustment(deal.$id, activeAdjustment.$id);
      setAdjustments((items) => [declined, ...items.filter((x) => x.$id !== declined.$id)]);
      toast({ title: "Addition declined", description: "The original order will continue unchanged." });
    } catch (error) {
      toast({ title: "Unable to decline", description: error instanceof Error ? error.message : "Please try again.", variant: "destructive" });
    } finally {
      setIsAdjustmentUpdating(false);
    }
  };

  const handleAdjustmentPayment = async () => {
    if (!deal || !activeAdjustment?.additionalAmount || isAdjustmentUpdating) return;
    const paymentContact = paymentContactFromIdentifier(user.emailOrPhone, paymentPhone);
    if (!paymentContact.phone || !window.Cashfree) {
      toast({
        title: !paymentContact.phone ? "Mobile number required" : "Payment system unavailable",
        description: !paymentContact.phone ? "Enter a valid mobile number to continue." : "Refresh the page and try again.",
        variant: "destructive",
      });
      return;
    }

    setIsAdjustmentUpdating(true);
    try {
      const pricing = await getDealAdjustmentPaymentPricing(activeAdjustment.$id);
      setAdjustmentPaymentPreview({ adjustmentId: activeAdjustment.$id, pricing });
    } catch (error) {
      toast({ title: "Unable to calculate payment", description: error instanceof Error ? error.message : "Please try again.", variant: "destructive" });
    } finally {
      setIsAdjustmentUpdating(false);
    }
  };

  const handleConfirmAdjustmentPayment = async () => {
    if (!deal || !adjustmentPaymentPreview || isAdjustmentUpdating) return;
    const paymentContact = paymentContactFromIdentifier(user.emailOrPhone, paymentPhone);
    if (!paymentContact.phone || !window.Cashfree) return;

    setIsAdjustmentUpdating(true);
    try {
      const order = await createDealAdjustmentPaymentOrder(
        deal.$id,
        adjustmentPaymentPreview.adjustmentId,
        adjustmentPaymentPreview.pricing.additionalAmount,
        { id: buyerId, name: user.name, email: paymentContact.email, phone: paymentContact.phone },
        sellerId,
        `${window.location.origin}/chats/${sellerId}`
      );
      if (!order.success || !order.order_id || !order.payment_session_id)
        throw new Error(order.error || "Unable to create the additional payment.");

      const cashfree = new window.Cashfree({ mode: "production" });
      const result = await cashfree.checkout({
        paymentSessionId: order.payment_session_id,
        returnUrl: `${window.location.origin}/chats/${sellerId}`,
        redirectTarget: "_modal",
      });
      if (result?.error) throw new Error(result.error.message || "Payment was not completed.");
      const verification = await verifyCashfreePayment({
        orderId: order.order_id,
        paymentId: result?.paymentDetails?.paymentId,
      });
      const verified = typeof verification === "boolean"
        ? verification
        : verification.success && verification.isValid;
      if (!verified) throw new Error("Cashfree has not confirmed the additional payment.");

      await refreshAdjustment();
      const refreshedDeal = await getDealByBuyerAndSeller(buyerId, sellerId, postId, true);
      if (refreshedDeal) setDeal(refreshedDeal);
      toast({ title: "Order updated", description: `₹${adjustmentPaymentPreview.pricing.additionalAmount.toFixed(2)} was added to the order.` });
      setAdjustmentPaymentPreview(null);
    } catch (error) {
      toast({ title: "Additional payment not completed", description: error instanceof Error ? error.message : "Please try again.", variant: "destructive" });
      await refreshAdjustment().catch(() => undefined);
    } finally {
      setIsAdjustmentUpdating(false);
    }
  };
  const handleDeliveryAddressSubmit = async () => {
    if (!deal || isSubmittingAddress) return;

    // Validate required fields
    if (!deliveryAddress.address || !deliveryAddress.mobile) {
      toast({
        title: "Missing Information",
        description: "Address and mobile number are required",
        variant: "destructive",
      });
      return;
    }
    setIsSubmittingAddress(true);
    // Validate for email
    const validationErrors = await validateDeliveryAddress(deliveryAddress);
    if (validationErrors.length > 0) {
      toast({
        title: "Invalid Information",
        description: validationErrors[0],
        variant: "destructive",
      });
      setIsSubmittingAddress(false);
      return;
    }

    try {
      await updateDealStatus(deal.$id, {
        deliveryAddress: JSON.stringify(deliveryAddress),
        status: "delivery-details-added",
        updatedAt: new Date().toISOString(),
      });

      await notificationService.createNotification({
        title: "Delivery Address Added",
        body: "Payer has provided delivery address. Check deal tracker for details.",
        type: "deal",
        recipient_id: sellerId,
        sender_id: buyerId,
      });

      setShowDeliveryModal(false);

      toast({
        title: "Address Added",
        description: "Delivery address has been shared with the provider",
      });
    } catch (error) {
      console.error("Failed to add delivery address:", error);
      toast({
        title: "Error",
        description: "Failed to add delivery address. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingAddress(false);
    }
  };
  return (
    <>
      <div className="p-3 rounded-2xl bg-white shadow-md mb-4 text-sm md:text-base flex flex-col gap-3">
        {isCollapsible && (
          <div
            className="flex items-center justify-between cursor-pointer mb-2"
            onClick={() => setIsCollapsed(!isCollapsed)}>
            <h3 className="font-medium text-blue-800 dark:text-blue-200">
              Deal Tracker
            </h3>
            <svg
              className={`w-5 h-5 text-blue-600 dark:text-blue-300 transition-transform duration-200 ${
                isCollapsed ? "rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        )}
        {!isCollapsed && (
          <div>
            <div className="text-gray-800 font-semibold text-base md:text-lg">
              🧾 Deal Progress Tracker
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Status:</span>
              <span className="capitalize font-medium text-blue-600">
                {deal.status === "dispute-raised" &&
                deal.disputeStatus === "resolved"
                  ? "dispute-resolved"
                  : deal.status === "payment-processing"
                    ? "Payment Processing"
                    : deal.status === "cancellation-requested"
                      ? "Cancellation Requested"
                      : deal.status === "refund-processing"
                        ? "Refund Processing"
                        : deal.status === "refund-failed"
                          ? "Refund Needs Attention"
                          : deal.status === "in-progress"
                            ? "In Progress"
                            : deal.status === "shipped"
                              ? "Shipped"
                              : deal.status}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-gray-700">Amount:</span>
              <span className="font-semibold text-green-600">
                ₹{deal.amount || 0}
              </span>
            </div>

            {showDisputeInfo && (
              <>
                <div className="text-red text-sm mt-1">
                  Dispute Reason: {deal.disputeReason}
                </div>
                <div className="text-red text-xs mt-1">
                  Evidence: Please share in the chat.
                </div>
                {deal.disputeRaisedAt && (
                  <div className="text-gray-500 text-xs mt-1">
                    Raised at: {new Date(deal.disputeRaisedAt).toLocaleString()}
                  </div>
                )}
              </>
            )}

            {(deal.status === "negotiation" ||
              deal.status === "payment-requested") &&
              parseFloat(deal.requestedAmount) > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Requested:</span>
                  <span className="text-orange-600 font-medium">
                    ₹{deal.requestedAmount} (INR)
                  </span>
                </div>
              )}

            {isSeller && sellerPayoutPreview &&
              !["refund-processing", "refund-failed", "cancelled"].includes(deal.status) && (
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 rounded-md bg-green-50 px-2.5 py-1.5">
                  <span className="text-xs font-medium text-green-800">
                    {deal.status === "completed"
                      ? "You received"
                      : deal.status === "cancellation-requested"
                        ? "Held pending cancellation"
                      : deal.status === "payment-processing"
                        ? "Payout processing"
                        : "You'll receive"}
                  </span>
                  <span className="ml-auto font-semibold text-green-700">
                    ₹{sellerPayoutPreview.youWillReceive.toFixed(2)}
                    <span className="ml-1.5 text-[11px] font-normal text-green-700/70">
                      {sellerPayoutPreview.isFirstDealFree
                        ? "no platform fee"
                        : `after ${sellerPayoutPreview.platformFeePercent.toFixed(1)}% fee`}
                    </span>
                  </span>
                </div>
              )}

            {activeAdjustment && (
              <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm text-violet-950">
                <div className="font-semibold">Add to Order</div>
                <div className="mt-1 text-xs text-violet-800">{activeAdjustment.description}</div>

                {activeAdjustment.status === "requested" && isSeller && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <input
                      type="number"
                      min="1"
                      max="500000"
                      step="0.01"
                      value={adjustmentQuote}
                      onChange={(event) => setAdjustmentQuote(event.target.value)}
                      placeholder="Additional amount"
                      className="min-w-0 flex-1 rounded-md border border-violet-300 bg-white px-3 py-2 text-gray-900"
                    />
                    <button
                      onClick={handleAdjustmentQuote}
                      disabled={isAdjustmentUpdating || Number(adjustmentQuote) < 1}
                      className="rounded-md bg-violet-600 px-3 py-2 text-white disabled:opacity-50">
                      Send Amount
                    </button>
                    <button onClick={handleAdjustmentDecline} disabled={isAdjustmentUpdating} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-700 disabled:opacity-50">
                      Decline
                    </button>
                  </div>
                )}

                {activeAdjustment.status === "requested" && isBuyer && (
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                    <span>Waiting for the provider to send the additional amount.</span>
                    <button onClick={handleAdjustmentDecline} disabled={isAdjustmentUpdating} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-700 disabled:opacity-50">
                      Withdraw
                    </button>
                  </div>
                )}

                {activeAdjustment.status === "quoted" && (
                  <div className="mt-2">
                    <div className="font-medium">Additional amount: ₹{activeAdjustment.additionalAmount?.toFixed(2)}</div>
                    {isBuyer ? (
                      <div className="mt-3 space-y-2">
                        {requiresPaymentPhone(user.emailOrPhone) && (
                          <PaymentPhoneField value={paymentPhone} onChange={setPaymentPhone} />
                        )}
                        <div className="flex gap-2">
                          <button onClick={handleAdjustmentPayment} disabled={isAdjustmentUpdating} className="rounded-md bg-violet-600 px-3 py-2 text-white disabled:opacity-50">
                            {isAdjustmentUpdating ? "Calculating..." : "Review & Pay"}
                          </button>
                          <button onClick={handleAdjustmentDecline} disabled={isAdjustmentUpdating} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-700 disabled:opacity-50">
                            Decline
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-1 text-xs">Waiting for the payer to pay or decline.</div>
                    )}
                  </div>
                )}

                {activeAdjustment.status === "payment-pending" && (
                  <div className="mt-2">
                    <div className="text-xs">Additional payment is awaiting Cashfree confirmation. Shipping and cancellation are temporarily paused.</div>
                    {isBuyer && (
                      <button onClick={handleAdjustmentPayment} disabled={isAdjustmentUpdating} className="mt-2 rounded-md bg-violet-600 px-3 py-2 text-white disabled:opacity-50">
                        {isAdjustmentUpdating ? "Calculating..." : "Review & Continue"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {isBuyer && isWorkDeal && deal.status === "paid" && (
              <div className="rounded-md bg-blue-50 px-2.5 py-1.5 text-xs text-blue-800">
                Payment is secured. Wait for the seller to deliver the work.
              </div>
            )}

            {deal.status === "in-progress" && (
              <div className="rounded-md bg-indigo-50 px-2.5 py-1.5 text-xs text-indigo-800">
                {isBuyer ? "The provider is working on your deal." : "Work is in progress. Mark it delivered when ready."}
              </div>
            )}

            {deal.status === "shipped" && (
              <div className="rounded-md bg-sky-50 px-2.5 py-1.5 text-xs text-sky-800">
                {isBuyer ? "The order has shipped. Confirm receipt when it arrives." : "Waiting for the payer to confirm receipt."}
              </div>
            )}

            {deal.status === "cancellation-requested" && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <div className="font-semibold">Cancellation requested</div>
                {deal.cancellationReason && <div className="mt-1">Reason: {deal.cancellationReason}</div>}
                <div className="mt-1 text-xs">
                  {isSeller
                    ? "Accept or decline the request. If you do not respond, the payer refund starts automatically."
                    : "The provider can accept or decline. If there is no response, your refund starts automatically."}
                </div>
                {cancellationTimeLeft !== null && cancellationTimeLeft > 0 && (
                  <div className="mt-1 text-xs font-medium">
                    ⏳ Response time remaining: {Math.floor(cancellationTimeLeft / 3600000)}h {Math.floor((cancellationTimeLeft % 3600000) / 60000)}m
                  </div>
                )}
                {cancellationTimeLeft === 0 && (
                  <div className="mt-1 text-xs font-medium">Refund is being queued automatically.</div>
                )}
              </div>
            )}

            {deal.status === "refund-processing" && (
              <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                Cashfree is processing the buyer refund. The deal will be cancelled only after confirmation.
              </div>
            )}

            {deal.status === "refund-failed" && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
                The refund needs support attention. Funds remain held and seller payout is blocked.
              </div>
            )}

            {deal.status === "cancelled" && (
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
                Deal cancelled - Cashfree confirmed the buyer refund.
              </div>
            )}

            {deal.status === "delivered" &&
              isBuyer &&
              timeLeft !== null &&
              timeLeft > 0 &&
              deal.disputeStatus !== "pending" && (
                <div className="text-sm text-gray-600">
                  ⏳ Time left to raise dispute:{" "}
                  {Math.floor(timeLeft / (1000 * 60 * 60))}h{" "}
                  {Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))}m
                </div>
              )}

            <div className="mt-2 flex flex-wrap items-center justify-start gap-2 [&>button]:w-auto [&>button]:shrink-0 [&>button]:whitespace-nowrap">
              {deal.status === "negotiation" && isSeller && (
                <button
                  onClick={handleRequestPayment}
                  disabled={isUpdating}
                  className="bg-yellow-500 text-white px-4 py-1.5 rounded-md hover:bg-yellow-600 transition disabled:opacity-50">
                  {isUpdating ? "Processing..." : "🧾 Request Payment"}
                </button>
              )}

              {deal.status === "payment-requested" && isBuyer && (
                <button
                  onClick={handlePaymentClick}
                  disabled={isUpdating}
                  className="bg-blue-500 text-white px-4 py-1.5 rounded-md hover:bg-blue-600 transition disabled:opacity-50">
                  {isUpdating
                    ? "Processing..."
                    : `💸 Pay ₹${deal.requestedAmount} (INR)`}
                </button>
              )}
              {deal.isDealRevised && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Deal Status:</span>
                  <span className="text-purple-600 font-medium text-sm">
                    🔄 Revised (Premium)
                  </span>
                </div>
              )}

              {showReviseButton && (
                <button
                  onClick={checkReviseEligibility}
                  disabled={isCheckingTrustScore}
                  className="bg-purple-500 text-white px-4 py-1.5 rounded-md hover:bg-purple-600 transition disabled:opacity-50 flex items-center gap-1">
                  {isCheckingTrustScore ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Checking...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      <span> Revise Deal</span>
                    </>
                  )}
                </button>
              )}
              {deal.status === "paid" && isSeller && isWorkDeal && (
                <button
                  onClick={() => handleStatusUpdate("in-progress")}
                  disabled={isUpdating}
                  className="bg-indigo-500 text-white px-4 py-1.5 rounded-md hover:bg-indigo-600 transition disabled:opacity-50">
                  {isUpdating ? "Processing..." : "▶ Start Work"}
                </button>
              )}

              {deal.status === "in-progress" && isSeller && isWorkDeal && (
                <button
                  onClick={() => handleStatusUpdate("delivered")}
                  disabled={isUpdating}
                  className="bg-green-500 text-white px-4 py-1.5 rounded-md hover:bg-green-600 transition disabled:opacity-50">
                  {isUpdating ? "Processing..." : "✅ Mark Work Delivered"}
                </button>
              )}

              {deal.status === "delivery-details-added" && isSeller && isPhysicalDeal && (
                <button
                  onClick={() => handleStatusUpdate("shipped")}
                  disabled={isUpdating || Boolean(activeAdjustment)}
                  className="bg-sky-600 text-white px-4 py-1.5 rounded-md hover:bg-sky-700 transition disabled:opacity-50">
                  {activeAdjustment ? "Resolve Addition Before Shipping" : isUpdating ? "Processing..." : "🚚 Mark as Shipped"}
                </button>
              )}

              {deal.status === "shipped" && isBuyer && isPhysicalDeal && (
                <button
                  onClick={() => handleStatusUpdate("delivered")}
                  disabled={isUpdating}
                  className="bg-green-600 text-white px-4 py-1.5 rounded-md hover:bg-green-700 transition disabled:opacity-50">
                  {isUpdating ? "Processing..." : "📦 Confirm Receipt"}
                </button>
              )}

              {deal.status === "paid" &&
                isBuyer &&
                hasResolvedPostCategory &&
                isPhysicalDeal &&
                !deal.deliveryAddress && (
                <button
                  onClick={() => setShowDeliveryModal(true)}
                  disabled={isUpdating}
                  className="bg-blue-500 text-white px-4 py-1.5 rounded-md hover:bg-blue-600 transition disabled:opacity-50">
                  📍 Add Delivery Address
                </button>
              )}
              {showCancellationButton && (
                <button
                  onClick={() => setShowCancellationForm(true)}
                  disabled={isUpdating}
                  className="bg-white text-rose-600 border border-rose-500 px-4 py-1.5 rounded-md hover:bg-rose-50 transition disabled:opacity-50">
                  Request Cancellation
                </button>
              )}
              {isBuyer && isPhysicalDeal && !activeAdjustment && (deal.status === "paid" || deal.status === "delivery-details-added") && (
                <button
                  onClick={() => setShowAdjustmentForm(true)}
                  disabled={isAdjustmentUpdating}
                  className="bg-violet-600 text-white px-4 py-1.5 rounded-md hover:bg-violet-700 transition disabled:opacity-50">
                  Add to Order
                </button>
              )}

              {isSeller && deal.status === "cancellation-requested" && (
                <>
                  <button
                    onClick={() => handleCancellationDecision(false)}
                    disabled={isUpdating}
                    className="bg-white text-gray-700 border border-gray-400 px-4 py-1.5 rounded-md hover:bg-gray-50 transition disabled:opacity-50">
                    Decline Cancellation
                  </button>
                  <button
                    onClick={() => handleCancellationDecision(true)}
                    disabled={isUpdating}
                    className="bg-rose-600 text-white px-4 py-1.5 rounded-md hover:bg-rose-700 transition disabled:opacity-50">
                    {isUpdating ? "Processing..." : "Accept & Refund"}
                  </button>
                </>
              )}
              {deal.deliveryAddress && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">
                      📍 Delivery Address
                    </h4>
                    {(() => {
                      try {
                        const address = JSON.parse(deal.deliveryAddress);
                        return (
                          <div className="text-sm space-y-1 text-gray-700">
                            <div>
                              <strong>Address:</strong> {address.address}
                            </div>
                            {address.landmark && (
                              <div>
                                <strong>Landmark:</strong> {address.landmark}
                              </div>
                            )}
                            <div>
                              {address.mobile && (
                                <div>
                                  <strong>Mobile:</strong> {address.mobile}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      } catch (e) {
                        return (
                          <div className="text-rose-500">
                            Error loading address
                          </div>
                        );
                      }
                    })()}
                  </div>
                )}

              {showDisputeButton && (
                <button
                  onClick={() => setShowDisputeForm(true)}
                  disabled={isUpdating}
                  className="bg-white text-red border border-rose-500 px-4 py-1.5 rounded-md hover:bg-slate-100 transition">
                  🚩 Raise Dispute
                </button>
              )}

              {showCompletionButton && (
                <button
                  onClick={() => handleStatusUpdate("completed")}
                  disabled={isUpdating || isReleasingPayment}
                  className="bg-purple-500 text-white px-4 py-1.5 rounded-md hover:bg-purple-600 transition disabled:opacity-50">
                  {isUpdating ? "Processing..." : "✅ Confirm Completion"}
                </button>
              )}

              {showResolveButton && (
                <>
                  <button
                    onClick={handleResolveDispute}
                    disabled={isUpdating}
                    className="bg-green-500 text-white px-4 py-1.5 rounded-md hover:bg-green-600 transition disabled:opacity-50">
                    {isUpdating ? "Processing..." : "✅ Continue Deal"}
                  </button>
                  <button
                    onClick={handleAcceptDisputeRefund}
                    disabled={isUpdating}
                    className="bg-rose-600 text-white px-4 py-1.5 rounded-md hover:bg-rose-700 transition disabled:opacity-50">
                    {isUpdating ? "Processing..." : "Accept Refund & Cancel"}
                  </button>
                </>
              )}

              {/* Payment Processing Status Display */}
              {deal.status === "payment-processing" && (
                <div className="mt-2 p-2 bg-orange-50 rounded-md w-full">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-700 font-semibold">
                        ✔️ {isBuyer ? "Work accepted" : "Payer accepted the work"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-orange-600 font-semibold">
                        ⏳ Seller payout processing
                      </span>
                      <span className="text-sm text-gray-500">
                        (usually within minutes; bank confirmation may take longer)
                      </span>
                      {isReleasingPayment && (
                        <span className="text-sm text-gray-500">
                          (Initiating...)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      The deal completes only after Cashfree confirms the seller was credited.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showCancellationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-4 max-w-md w-full">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-gray-900">Request Cancellation</h3>
              <button onClick={() => setShowCancellationForm(false)}>
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Tell the seller why you want to cancel. They have up to 24 hours to respond; otherwise your refund starts automatically.
            </p>
            <textarea
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              placeholder="Explain why you want to cancel..."
              maxLength={500}
              className="w-full border rounded-md p-2 mb-1 h-28 text-gray-700"
            />
            <div className="text-right text-xs text-gray-500 mb-4">
              {cancellationReason.length}/500
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCancellationForm(false)}
                className="px-4 py-2 border rounded-md hover:bg-gray-50 text-gray-700">
                Keep Deal
              </button>
              <button
                onClick={handleCancellationRequest}
                disabled={cancellationReason.trim().length < 10 || isUpdating}
                className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700 disabled:opacity-50">
                {isUpdating ? "Submitting..." : "Send Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdjustmentForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Add to Order</h3>
              <button onClick={() => setShowAdjustmentForm(false)}><X className="h-5 w-5 text-gray-600" /></button>
            </div>
            <p className="mb-3 text-sm text-gray-600">Describe what you want to add. The provider will confirm the additional amount before you pay.</p>
            <textarea
              value={adjustmentDescription}
              onChange={(event) => setAdjustmentDescription(event.target.value)}
              maxLength={500}
              placeholder="Example: Add one more blue shirt, size M"
              className="h-28 w-full rounded-md border p-2 text-gray-900"
            />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setShowAdjustmentForm(false)} className="rounded-md border px-4 py-2 text-gray-700">Cancel</button>
              <button onClick={handleAdjustmentRequest} disabled={adjustmentDescription.trim().length < 5 || isAdjustmentUpdating} className="rounded-md bg-violet-600 px-4 py-2 text-white disabled:opacity-50">
                {isAdjustmentUpdating ? "Sending..." : "Send Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {adjustmentPaymentPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white text-gray-900 shadow-xl">
            <div className="flex items-center justify-between border-b p-4">
              <h2 className="text-lg font-semibold">Additional Payment</h2>
              <button
                onClick={() => setAdjustmentPaymentPreview(null)}
                disabled={isAdjustmentUpdating}
                className="rounded-full p-1 hover:bg-gray-100 disabled:opacity-50">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="space-y-3 p-4 text-sm">
              {adjustmentPaymentPreview.pricing.isFirstDealFree && (
                <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-green-800">
                  <Gift className="h-5 w-5" />
                  <span className="font-semibold">First deal: platform fee waived</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Additional order amount</span>
                <span className="font-medium">₹{adjustmentPaymentPreview.pricing.additionalAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="flex items-center text-gray-600">
                  <Shield className="mr-1 h-3.5 w-3.5" />
                  Platform fee ({adjustmentPaymentPreview.pricing.buyerFeePercent.toFixed(1)}%)
                </span>
                <span className="font-medium">₹{adjustmentPaymentPreview.pricing.buyerPlatformFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-t pt-3 text-base font-semibold">
                <span>Total to pay</span>
                <span>₹{adjustmentPaymentPreview.pricing.totalToPay.toFixed(2)}</span>
              </div>
              <div className="rounded-lg bg-yellow-50 p-3 text-xs text-yellow-800">
                <Shield className="mr-1 inline h-3.5 w-3.5" />
                The additional order amount is protected in escrow with your existing deal.
              </div>
            </div>
            <div className="flex gap-3 border-t bg-gray-50 p-4">
              <button
                onClick={() => setAdjustmentPaymentPreview(null)}
                disabled={isAdjustmentUpdating}
                className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-gray-700 disabled:opacity-50">
                Cancel
              </button>
              <button
                onClick={handleConfirmAdjustmentPayment}
                disabled={isAdjustmentUpdating}
                className="flex-1 rounded-md bg-violet-600 px-4 py-2 text-white hover:bg-violet-700 disabled:bg-gray-400">
                {isAdjustmentUpdating
                  ? "Processing..."
                  : `Pay ₹${adjustmentPaymentPreview.pricing.totalToPay.toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispute Form */}
      {showDisputeForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-4 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">Raise Dispute</h3>
              <button onClick={() => setShowDisputeForm(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              placeholder="Explain the issue and provide any evidence..."
              className="w-full border rounded-md p-2 mb-4 h-32 text-gray-700"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDisputeForm(false)}
                className="px-4 py-2 border rounded-md hover:bg-gray-50 text-gray-700">
                Cancel
              </button>
              <button
                onClick={handleDisputeSubmit}
                disabled={!disputeReason.trim() || isUpdating}
                className="px-4 py-2 bg-rose-500 text-white rounded-md hover:bg-rose-600 disabled:opacity-50">
                {isUpdating ? "Submitting..." : "Submit Dispute"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Breakdown Dialog - UPDATED UI */}
      {showPaymentDialog && paymentBreakdown && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                Payment Details
              </h2>
              <button
                onClick={() => setShowPaymentDialog(false)}
                className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Order Value Header */}
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Order Value</p>
                <p className="text-2xl font-bold text-slate-800">
                  ₹{paymentBreakdown.originalAmount.toLocaleString("en-IN")}
                </p>
              </div>

              {/* Protection Checklist Animation */}
              {showProtectionChecklist && (
                <ProtectionChecklist
                  type="buyer"
                  amount={paymentBreakdown.originalAmount}
                  onAnimationComplete={handleBuyerProtectionComplete}
                />
              )}

              {/* Payment Details (shown after protection animation) */}
              {showPaymentDetails && (
                <>
                  {/* First Deal Free Badge */}
                  {paymentBreakdown.isFirstDealFree && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                      <Gift className="w-5 h-5 text-green-600" />
                      <div>
                        <span className="text-green-800 font-semibold text-sm">First Deal Free!</span>
                        <p className="text-green-700 text-xs">Platform fee waived for your first deal.</p>
                      </div>
                    </div>
                  )}

                  {/* Payment Breakdown */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Payment Breakdown
                </h4>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Requested Amount</span>
                    <span className="text-gray-900 font-medium">
                      ₹{paymentBreakdown.originalAmount.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600 flex items-center">
                      <Shield className="w-3 h-3 mr-1" />
                      Platform Fee ({paymentBreakdown.platformFeePercent.toFixed(1)}%)
                    </span>
                    {paymentBreakdown.isFirstDealFree ? (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 line-through text-xs">
                          ₹{(paymentBreakdown.originalAmount * 0.014).toFixed(2)}
                        </span>
                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-semibold">
                          FREE
                        </span>
                      </div>
                    ) : (
                      <span className="text-green-600 font-medium">
                        ₹{paymentBreakdown.platformFee.toFixed(2)}
                      </span>
                    )}
                  </div>

                  <div className="border-t pt-2">
                    <div className="flex justify-between font-medium">
                      <span>Subtotal</span>
                      <span>
                        ₹
                        {(
                          paymentBreakdown.originalAmount +
                          paymentBreakdown.platformFee
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {paymentBreakdown.canApplyDiscount && (
                    <div className="border-t pt-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-blue-600 flex items-center">
                          <Wallet className="w-3 h-3 mr-1" />
                          Trust Score Discount
                        </span>
                        <span className="font-medium text-blue-600">
                          -₹{paymentBreakdown.discount.toFixed(2)}
                        </span>
                      </div>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyDiscount}
                          onChange={(e) => setApplyDiscount(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          Apply 10% discount cap (
                          {Math.round(paymentBreakdown.discount * 10)} points
                          will be deducted)
                        </span>
                      </label>
                    </div>
                  )}

                  <div className="border-t pt-2 flex justify-between font-semibold text-lg">
                    <span>Total Amount</span>
                    <span>
                      ₹
                      {buyerPayableAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Trust Score Info */}
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <Wallet className="w-4 h-4 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-900">
                    Your Benefits
                  </span>
                </div>
                <p className="text-xs text-blue-700 mb-1">
                  Trust score: {paymentBreakdown.trustScorePoints} pts |
                  Discount available: ₹{paymentBreakdown.maxTrustDiscountAvailable.toFixed(2)}
                </p>
                <p className="text-xs text-blue-700">
                  {paymentBreakdown.isFirstDealFree
                    ? "🎉 First deal free - Platform fee waived!"
                    : `Platform fee: ${paymentBreakdown.platformFeePercent.toFixed(1)}% (lower for higher amounts)`
                  }
                </p>
              </div>

              {/* Escrow Notice */}
              <div className="bg-yellow-50 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <Shield className="w-3 h-3 inline mr-1" />
                  Your payment is secured in escrow until delivery is confirmed.
                </p>
              </div>

              {requiresPaymentPhone(user.emailOrPhone) && (
                <PaymentPhoneField value={paymentPhone} onChange={setPaymentPhone} />
              )}

              {/* Warning if discount not applied */}
              {paymentBreakdown.canApplyDiscount && !applyDiscount && (
                <div className="bg-rose-50 rounded-lg p-3">
                  <p className="text-xs text-rose-800 flex items-start">
                    <AlertCircle className="w-3 h-3 inline mr-1 mt-0.5 flex-shrink-0" />
                    <span>
                      You have unused trust score points! Applying the discount
                      will save you ₹{paymentBreakdown.discount.toFixed(2)}.
                    </span>
                  </p>
                </div>
              )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex space-x-3">
              <button
                onClick={() => setShowPaymentDialog(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleConfirmPayment}
                disabled={isProcessing || (requiresPaymentPhone(user.emailOrPhone) && !paymentContactFromIdentifier(user.emailOrPhone, paymentPhone).phone)}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors flex items-center justify-center">
                {isProcessing ? (
                  <>
                    <Loader />
                    <span className="ml-2">Processing...</span>
                  </>
                ) : (
                  `Pay ₹${buyerPayableAmount.toFixed(2)}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Address Modal */}
      {showDeliveryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                Add Delivery Address
              </h2>
              <button
                onClick={() => setShowDeliveryModal(false)}
                className="p-1 text-gray-700 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="bg-yellow-50 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  📍 Provide complete address with city, state, pincode.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Complete Address *
                </label>
                <textarea
                  value={deliveryAddress.address}
                  onChange={(e) =>
                    setDeliveryAddress({
                      ...deliveryAddress,
                      address: e.target.value,
                    })
                  }
                  className="w-full border text-gray-700 rounded-md p-2 text-sm h-20"
                  placeholder="House/Flat no, Street, Area, City, State, Pincode"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  value={deliveryAddress.mobile}
                  onChange={(e) =>
                    setDeliveryAddress({
                      ...deliveryAddress,
                      mobile: e.target.value,
                    })
                  }
                  className="w-full border text-gray-700 rounded-md p-2 text-sm"
                  placeholder="10-digit mobile number"
                  maxLength={10}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Landmark
                </label>
                <input
                  type="text"
                  value={deliveryAddress.landmark}
                  onChange={(e) =>
                    setDeliveryAddress({
                      ...deliveryAddress,
                      landmark: e.target.value,
                    })
                  }
                  className="w-full text-gray-700 border rounded-md p-2 text-sm"
                  placeholder="Nearby landmark (Optional)"
                />
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 flex space-x-3">
              <button
                onClick={() => setShowDeliveryModal(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleDeliveryAddressSubmit}
                disabled={isSubmittingAddress}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors">
                {isSubmittingAddress ? "Saving..." : "Save Address"}
              </button>
            </div>
          </div>
        </div>
      )}

      {ENABLE_BUYER_DEAL_REVISION && showReviseDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Revise Deal
                </h2>
                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                  Premium
                </span>
              </div>
              <button
                onClick={() => setShowReviseDialog(false)}
                className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content - Only upgrade prompt */}
            <div className="p-4 space-y-4">
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-800 font-medium">
                    Premium Feature Required
                  </span>
                </div>
                <p className="text-amber-700 text-sm">
                  Your trust score: {buyerTrustScore || 0} points
                  <br />
                  Required: 5,000 points
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="font-medium text-gray-900">
                  Upgrade to Premium and get:
                </h3>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <RefreshCw className="w-4 h-4 text-purple-500 mt-0.5" />
                    <span>
                      <strong>Deal Revision:</strong> Renegotiate price after
                      payment but before shipment.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-purple-500 mt-0.5" />
                    <span>
                      <strong>Busiconn Agent Support:</strong> Priority dispute,
                      Instant refund resolution
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Wallet className="w-4 h-4 text-purple-500 mt-0.5" />
                    <span>
                      <strong>Credits:</strong> Pay now, and get the same amount
                      free for your future purchases!
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CreditCard className="w-4 h-4 text-purple-500 mt-0.5" />
                    <span>
                      <strong>Lower Fees:</strong> Just 2% platform fee
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-3">
                <p className="text-purple-800 text-sm font-medium">
                  💎 Buy {5000 - (buyerTrustScore || 0)} more points to unlock
                  premium features
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex space-x-3">
              <button
                onClick={() => setShowReviseDialog(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={() => navigate("/trust-score")}
                className="flex-1 py-2 px-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-md hover:from-purple-700 hover:to-blue-700 transition-colors flex items-center justify-center gap-2">
                <Crown className="w-4 h-4" />
                <span>Buy Points</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seller Payment Request Modal */}
      <SellerPaymentRequestModal
        isOpen={showSellerPaymentModal}
        onClose={() => setShowSellerPaymentModal(false)}
        onSubmit={handlePaymentRequestSubmit}
        sellerId={sellerId}
        dealId={deal?.$id || ""}
      />
    </>
  );
};
